import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { tenantKeys, invoiceKeys } from "@/lib/queryKeys";
import { importRecibidasXml } from "@/services/platformTenantsService";
import type { ImportCfeResult } from "@/services/invoiceService";

const SUCCESS_TOAST_OPTIONS = {
  style: { background: "#f0fdf4", color: "#14532d" } as const,
  iconTheme: { primary: "#22c55e", secondary: "#f0fdf4" } as const,
};

export interface ImportRecibidasCfeDialogProps {
  tenantId: string;
  tenantName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  /** When provided (e.g. from Settings), use this instead of platform API. Invalidates current tenant + invoices. */
  importFn?: (files: File[]) => Promise<ImportCfeResult>;
}

export function ImportRecibidasCfeDialog({
  tenantId,
  tenantName,
  open,
  onOpenChange,
  onSuccess,
  importFn,
}: ImportRecibidasCfeDialogProps): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);

  const mutation = useMutation({
    mutationFn: (filesToUpload: File[]) =>
      importFn
        ? importFn(filesToUpload)
        : importRecibidasXml(tenantId, filesToUpload),
    onSuccess: (data) => {
      const hasErrors = data.errors.length > 0;
      if (hasErrors) {
        toast.success(
          t("tenants.page.importRecibidasDialog.successWithErrors", {
            imported: data.imported,
            skipped: data.skipped,
            count: data.errors.length,
          }),
          { ...SUCCESS_TOAST_OPTIONS }
        );
        data.errors.forEach((e) => {
          const msg =
            e.code === "wrong_type_use_emitted"
              ? t("tenants.page.importRecibidasDialog.errorWrongTypeUseEmitted")
              : e.message;
          toast.error(e.filename ? `${e.filename}: ${msg}` : msg, {
            duration: 5000,
          });
        });
      } else {
        const messageKey =
          data.skipped === 0
            ? "tenants.page.importRecibidasDialog.successImportedOnly"
            : data.imported === 0
              ? "tenants.page.importRecibidasDialog.successAllSkipped"
              : "tenants.page.importRecibidasDialog.success";
        toast.success(
          t(messageKey, {
            imported: data.imported,
            skipped: data.skipped,
          }),
          { ...SUCCESS_TOAST_OPTIONS }
        );
      }
      setFiles([]);
      onOpenChange(false);
      if (importFn) {
        queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
        queryClient.invalidateQueries({ queryKey: invoiceKeys.recibidas() });
      } else {
        queryClient.invalidateQueries({ queryKey: tenantKeys.overview(tenantId) });
      }
      onSuccess?.();
    },
    onError: () => {
      toast.error(t("tenants.page.importRecibidasDialog.error"));
    },
  });

  const handleClose = (): void => {
    if (!mutation.isPending) {
      setFiles([]);
      onOpenChange(false);
    }
  };

  const handleSubmit = (): void => {
    if (files.length === 0) return;
    mutation.mutate(files);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("tenants.page.importRecibidasDialog.title")}
          </DialogTitle>
          <DialogDescription>
            {t("tenants.page.importRecibidasDialog.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="import-recibidas-files">
              {t("tenants.page.importRecibidasDialog.fileLabel")}
            </Label>
            <Input
              id="import-recibidas-files"
              type="file"
              accept=".xml,.zip"
              multiple
              className="cursor-pointer"
              onChange={(e) => {
                const list = e.target.files;
                if (list) setFiles(Array.from(list));
              }}
              disabled={mutation.isPending}
            />
            {files.length > 0 ? (
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="truncate">{f.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 shrink-0 px-1"
                      onClick={() =>
                        setFiles((prev) => prev.filter((_, j) => j !== i))
                      }
                      disabled={mutation.isPending}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose()}
            disabled={mutation.isPending}
          >
            {t("tenants.page.importRecibidasDialog.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={files.length === 0 || mutation.isPending}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("tenants.page.importRecibidasDialog.submitting")}
              </>
            ) : (
              t("tenants.page.importRecibidasDialog.submit")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
